/*
 * validacionCliente.js — Lógica pura del formulario de cliente (C-026, ver diseno/CAMBIOS.md).
 *
 * Correos como chips (se guardan igual que antes: una lista separada por comas en `email`),
 * validación de cada campo con su mensaje y la sugerencia para nombres todo en mayúsculas.
 */

// ───────────── Correos ─────────────

export const correoValido = (correo) => /^[^\s@,;]+@[^\s@,;]+\.[a-z]{2,}$/i.test(correo);

/** "a@x.com, b@y.com; c@z.com" → ['a@x.com', 'b@y.com', 'c@z.com'] */
export const separarCorreos = (texto) =>
  String(texto || '').split(/[,;\s]+/).map(c => c.trim()).filter(Boolean);

/** Agrega los correos del texto a la lista, sin repetir (sin distinguir mayúsculas). */
export const agregarCorreos = (lista, texto) => {
  const resultado = [...lista];
  for (const c of separarCorreos(texto)) {
    if (!resultado.some(x => x.toLowerCase() === c.toLowerCase())) resultado.push(c);
  }
  return resultado;
};

export const correosAlGuardar = (lista) => lista.join(', ');

export const mensajeCorreos = (lista) => {
  const malos = lista.filter(c => !correoValido(c));
  if (!malos.length) return null;
  return malos.length === 1
    ? `"${malos[0]}" no parece un correo. Corrígelo o quítalo.`
    : `${malos.length} correos no parecen válidos. Corrígelos o quítalos.`;
};

// ───────────── Mayúsculas en el nombre ─────────────

const SIGLAS = [
  [/\bS\.?\s?A\.?\s?P\.?\s?I\.?(?=\s|$|,)/gi, 'S.A.P.I.'],
  [/\bS\.?\s?A\.?(?=\s|$|,)/gi, 'S.A.'],
  [/\bC\.?\s?V\.?(?=\s|$|,)/gi, 'C.V.'],
  [/\bS\.?\s?de\s?R\.?\s?L\.?(?=\s|$|,)/gi, 'S. de R.L.'],
  [/\bS\.?\s?C\.?(?=\s|$|,)/gi, 'S.C.'],
  [/\bA\.?\s?C\.?(?=\s|$|,)/gi, 'A.C.'],
];
const MINUSCULAS = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'e', 'en', 'el']);

export const esTodoMayusculas = (texto) => {
  const t = String(texto || '').trim();
  return t.length > 3 && t === t.toUpperCase() && /[A-ZÁÉÍÓÚÑÜ]/.test(t);
};

/**
 * Cómo escribir un nombre que está todo en mayúsculas: mayúscula inicial en cada palabra,
 * conectores en minúsculas y las siglas de sociedad con puntos. Nunca se aplica sola.
 */
export const sugerirNombre = (texto) => {
  let s = String(texto || '').trim().toLowerCase().split(/\s+/)
    .map((w, i) => (i > 0 && MINUSCULAS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
  for (const [re, rep] of SIGLAS) s = s.replace(re, rep);
  return s;
};

/** La sugerencia que se muestra al salir del campo, o null. */
export const sugerenciaNombre = (texto) => {
  if (!esTodoMayusculas(texto)) return null;
  const sug = sugerirNombre(texto);
  return sug !== String(texto).trim() ? sug : null;
};

// ───────────── Validación al guardar ─────────────

/**
 * Errores por campo, en el orden del formulario: [{ campo, mensaje, resumen }].
 * `correoPendiente`: lo que quedó escrito en el campo de correos sin convertirse en chip.
 */
export const validarCliente = (datos, correos) => {
  const errores = [];
  const nombre = String(datos.nombre || '').trim();
  if (!nombre) errores.push({ campo: 'nombre', mensaje: 'Escribe el nombre del cliente.', resumen: 'falta el nombre' });
  else if (nombre.length < 3) errores.push({ campo: 'nombre', mensaje: 'Escribe al menos 3 letras.', resumen: 'el nombre es muy corto' });
  if (!datos.contador_id) errores.push({ campo: 'contador', mensaje: 'Elige quién lleva al cliente.', resumen: 'falta el contador' });
  if (!datos.regimen) errores.push({ campo: 'regimen', mensaje: 'Elige el régimen fiscal.', resumen: 'falta el régimen' });
  if (!datos.industria) errores.push({ campo: 'industria', mensaje: 'Elige la industria.', resumen: 'falta la industria' });
  const msgCorreos = mensajeCorreos(correos);
  if (msgCorreos) errores.push({ campo: 'correos', mensaje: msgCorreos, resumen: 'hay un correo que no es válido' });
  const tel = String(datos.telefono || '').replace(/\D/g, '');
  if (tel && tel.length !== 10) errores.push({ campo: 'telefono', mensaje: 'Deben ser 10 dígitos.', resumen: 'el teléfono no tiene 10 dígitos' });
  return errores;
};

export const resumenErrores = (errores) =>
  errores.length ? `No se guardó: ${errores.map(e => e.resumen).join(', ')}.` : '';
